import type { Dict } from "../core.js";
import type { integrations as vi } from "../vi/integrations.js";

export const integrations: Dict<typeof vi> = {
  technicalDocuments: "Technical documents",
  commercialDocuments: "Contracts and commercial documents",
  docsIntro: "Open source documents or add a copy provided to Bube. Adding a document does not grant access.",
  docsAdd: "Add a document",
  docsReferences: "Reference sources",
  docsOfficial: "Official source",
  docsThirdParty: "Third-party reference",
  docsAdded: "Added documents",
  docsEmpty: "No additional files or URLs yet.",
  docsAddedBy: "Added by {actor}",
  docsTitle: "Document title",
  docsKind: "Add as",
  docsUpload: "Upload a file",
  docsLink: "Add a URL",
  docsFile: "Choose a document file",
  docsUrl: "Document URL (HTTPS)",
  docsFileLimit: "PDF, DOCX, XLSX, TXT or MD; between 1 byte and 5 MiB.",
  bkavPrivateDocument: "BKAV provides FAQ_WebServices_Bkav directly to partners. The catalog has no public URL for this document. Add the file or URL supplied by BKAV here.",
  guidancePurpose: "Purpose",
  bkavPurpose: "Connect to BKAV eHoadon to issue, look up and download e-invoices using supported capabilities.",
  productPurpose: "Configure Bube’s provider connection and manage the apps that may use it.",
  guidanceConditions: "Requirements",
  productConditions: "Configure the correct environment, pass validation and obtain the required access before use. Check access and readiness for the current state; reference documents do not replace approval.",
  guidanceRights: "View service access",
  guidanceTechnical: "Technical connection details",
  guidanceProtocol: "Protocol",
  guidanceEndpoint: "Service path",
  guidanceSource: "Implementation sources",
  bkavSource: "Partner documentation provided by BKAV; the third-party libraries below are reference material only.",

  country: "Country / market",
  filterEnvironment: "Environment",
  allEnvironments: "All environments",
  filterStatus: "Configuration state",
  allStates: "All states",
  stateEnabled: "Enabled",
  stateDisabled: "Not enabled",
  statusFilterHint: "State is scoped to the selected app and environment. Enabled configuration still requires access and validation. Upcoming products appear when all environments and all states are selected.",

  title: "Integrations",
  titleForApp: "Integrations · {app}",
  introBefore: "Manage ",
  introOfBube: "Bube's",
  introMiddle: " provider accounts. Each configuration change needs ",
  introShared: "separate testing and activation",
  introAfter: ". Apps only use activated configurations; lab accounts cannot be assigned to apps.",
  onlyForApp: "Showing only products {app} can use.",
  officialBefore: "Fixed by the ",
  officialLink: "official documentation",
  officialAfter: " — change it in code, not here.",
  allServices: "All services",
  serviceFilterAria: "Filter by service type",
  emptyForFilter: "No integrations match the filter.",
  marketGlobal: "Global",
  allMarkets: "All markets",
  marketFilterAria: "Filter by market",
  seeAll: "See all integrations",
  loadFailed: "Couldn't load the integrations list.",
  noSecretsKeyBefore: "The hub has no ",
  noSecretsKeyAfter: " yet — secret fields can't be saved, and apps can't read any product that has secret fields.",
  emptyForApp: "This app doesn't use any partner integrations.",
  appChipTitle: "{app} integrations",
  serviceCount: "{n} services",
  serviceCountOne: "1 service",
  enabledCount: "{n} enabled",
  configure: "Configure",
  usedBy: "Used by",
  environment: "Environment",
  inUse: "Usage",
  notOnHub: "Not on hub yet",
  tenants: "customers",
  lastActivity: "last {when}",
  costMonth: "Estimated partner cost this month: {amount} ({count} transactions)",
  costPrevMonth: "Last month: {amount}",
  costQuotaWarn: "{pct}% of this month's transaction quota used",
  appNotConnected: "App not connected",
  usageReadFailed: "Couldn't read",
  "env.unsupported": "Not supported",
  "env.enabled": "Enabled",
  "env.alwaysOn": "Always on",
  "env.alwaysOnMissing": "Always on · missing fields",
  "env.completeOff": "Complete · off",
  "env.missing": "Missing fields",
  "env.notConfigured": "Not configured",
  "envTab.sandbox": "Sandbox (test)",
  "envTab.live": "Live (production)",
  dialogTitle: "Configure {name}",
  dialogUsedBy: "used by {apps}",
  sharedNotice: "Shared by {n} apps ({apps}): each app has its own Connection binding. A candidate does not replace the serving version. Activating a version can affect every app bound to that Connection.",
  envUnsupportedTitle: "{name} has no such environment",
  keepSecretPlaceholder: "Leave blank to keep current",
  emptySecretPlaceholder: "Not set",
  saved: "Saved.",
  savedAndEnabled: "Saved and ENABLED.",
  disabledDone: "DISABLED.",
  saveFailed: "Couldn't save.",
  testFailed: "Couldn't run the test.",
  stateOn: "ENABLED",
  stateOff: "Disabled",
  requiredComplete: "required fields complete",
  requiredIncomplete: "required fields incomplete",
  testAuthTitle: "Call the partner to verify the credentials",
  testReachTitle: "Only checks that the partner's server responds",
  testing: "Testing…",
  testCredentials: "Test credentials",
  testConnection: "Test connection",
  confirmDisableMulti: "Disable for every customer of {n} apps?",
  confirmDisable: "Disable for every customer?",
  disable: "Disable",
  saveAndEnable: "Save & enable",
  "test.authOk": "Credentials VALID",
  "test.reachOk": "Server responded — credentials not verified",
  "test.failed": "Failed",
  "test.inconclusive": "Inconclusive",
  "inbound.title": "Production webhooks — paste into the partner portal",
  "inbound.intro": "The partner calls the app DIRECTLY, not the hub. Each key set has a single webhook URL ⇒ the production key must point at the URL below; the lab uses its own key set.",
  "inbound.envLive": "Live",
  "inbound.envSandbox": "Sandbox",
  "inbound.channelIpn": "IPN",
  "inbound.channelEvents": "Events",
  "inbound.copy": "Copy",
  "inbound.copied": "Copied",
};
